import { useMemo } from 'react';
import type { EnrichedClientDto, ItemSettingsDto, SaEngineRequestData } from '../../generated/client';
import {
    useGeneralProjectionSettings,
    useProjectionCashEventSettings,
    useProjectionCashFlowSettings,
    useProjectionExternalAssetSettings,
    useProjectionInternalAssetSettings,
} from '../../projection-settings-api';
import { useSurplusSettings } from '../../surplus-api';

export function useProjectionSetup(projectionPeriodInYears?: number, client?: EnrichedClientDto) {
    const { data: generalSettings } = useGeneralProjectionSettings();
    if (projectionPeriodInYears && generalSettings) {
        generalSettings.projectionPeriodYears = projectionPeriodInYears;
    }
    const { data: surplus } = useSurplusSettings();
    const { data: cashEvents } = useProjectionCashEventSettings();
    const { data: cashFlows } = useProjectionCashFlowSettings();
    const { data: internalAssets, isSuccess: isInternalAssetsSettingsSuccess } = useProjectionInternalAssetSettings();
    const { data: externalAssets, isSuccess: isExternalAssetSettingsSuccess } = useProjectionExternalAssetSettings();

    const cashFlow = surplus?.enabled ? surplus?.itemsSettings : cashFlows?.itemsSettings;

    const enabledCashEvents = cashEvents?.enabled
        ? cashEvents?.itemsSettings
              .filter((cashEvent) => cashEvent.enabled && !cashEvent.hidden)
              .map((enabledCashEvent) => ({
                  amount: enabledCashEvent.amount!,
                  dueDate: enabledCashEvent.effectiveDate!,
              }))
        : [];

    const enabledBankableAssets = useMemo(() => {
        if (isInternalAssetsSettingsSuccess && isExternalAssetSettingsSuccess && (internalAssets?.enabled || externalAssets?.enabled)) {
            return [...(internalAssets?.enabled ? internalAssets.itemsSettings : []), ...(externalAssets?.enabled ? externalAssets.itemsSettings : [])]
                .filter((asset: ItemSettingsDto) => asset.enabled)
                .map((enabledAsset) => ({
                    amount: enabledAsset.amount!,
                    investmentStrategy: enabledAsset.investmentStrategy!,
                }));
        } else {
            return [];
        }
    }, [isInternalAssetsSettingsSuccess, isExternalAssetSettingsSuccess, internalAssets, externalAssets]);

    const enabledCashFlows =
        cashFlow && cashFlows?.enabled
            ? cashFlow
                  ?.filter((cashflow) => cashflow.enabled)
                  .map((enabledCashFlow) => ({
                      amount: enabledCashFlow.surplusType === 'EXPENSE' ? enabledCashFlow.amount! * -1 : enabledCashFlow.amount!,
                      fromDate: enabledCashFlow.fromDate ?? '',
                      toDate: enabledCashFlow.toDate ?? '',
                      incomeExpenseSurplus: !!enabledCashFlow.surplusType,
                  }))
            : [];

    const isEnabled = !!client && !!generalSettings && !!surplus && enabledBankableAssets.length > 0 && !!cashEvents && !!cashFlows;

    return {
        body: {
            bankableAssets: enabledBankableAssets,
            cashEvents: enabledCashEvents,
            recurringCashflows: enabledCashFlows,
            simulationSettings: generalSettings!,
        } as SaEngineRequestData,
        enabled: isEnabled,
    };
}
